import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";
import { Info as InfoIcon } from "@phosphor-icons/react/dist/ssr/Info";
import { X as XIcon } from "@phosphor-icons/react/dist/ssr/X";
import { FileDropzone } from "~ui-components/components/atoms/file-dropzone";
import { map } from "lodash";
import { BASIC_MARKERS } from "../basic-makers";

import React, { useState } from "react";

export const PREDEFINED_MARKERS = {
	...BASIC_MARKERS,
};

export interface SettingsDrawerProps {
	onClose?: () => void;
	open?: boolean;
	onChange: (value: string) => void;
	onAddCustomMarkerIcon: (icon: File) => void;
	value: string | undefined;
	customIcons: IconFile[];
}

interface IconFile {
	id: string | null;
	accountId: string | null;
	name: string;
	iconUrl: string; // AWSURL maps to string in TypeScript
	size: string;
}

export function MarkerSettingsDrawer({
	onClose,
	open,
	onChange,
	onAddCustomMarkerIcon,
	value,
	customIcons,
}: SettingsDrawerProps) {
	const [tab, setTab] = useState<string>("basic");
	const [icons, setIcons] = useState<IconFile[]>([]);

	React.useEffect(() => {
		if (Array.isArray(customIcons)) {
			setIcons(customIcons);
		}
	}, [customIcons]);

	// prettier-ignore
	const checkImageSize = (file: File): Promise<boolean> => {
		return new Promise((resolve) => {
			const img = new Image();
			img.onload = () => {
				resolve(
					(img.width === 32 && img.height === 32) ||
						(img.width === 50 && img.height === 50),
				);
			};
			img.src = URL.createObjectURL(file);
		});
	};
	// prettier-ignore
	const getImageSize = (file: File): Promise<string> => {
		return new Promise((resolve) => {
			const img = new Image();
			img.onload = () => {
				resolve(`${img.width}x${img.height}`);
			};
			img.src = URL.createObjectURL(file);
		});
	};

	const handleIconUpload = (file: File): string => {
		onAddCustomMarkerIcon(file);
		return URL.createObjectURL(file);
	};

	const handleCoverDrop = React.useCallback(async ([file]: File[]) => {
		if (!file) return;

		const isValid = await checkImageSize(file);
		if (!isValid) {
			alert("Only 32x32 or 50x50 PNG images are accepted.");
			return;
		}
		const sizeStr = await getImageSize(file);
		const s3Url = handleIconUpload(file); // This is sync, no need to await

		setIcons((prev) => [
			...prev,
			{
				name: file.name,
				iconUrl: s3Url,
				size: sizeStr,
				id: null,
				accountId: null,
			},
		]);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<Drawer
			ModalProps={{ BackdropProps: { invisible: false }, sx: { zIndex: 1400 } }}
			PaperProps={{
				elevation: 24,
				sx: {
					display: "flex",
					flexDirection: "column",
					maxWidth: "100%",
					width: "440px",
				},
			}}
			anchor="right"
			disableScrollLock
			onClose={onClose}
			open={open}>
			<Stack
				direction="row"
				spacing={3}
				sx={{
					alignItems: "center",
					justifyContent: "space-between",
					px: 3,
					pt: 2,
				}}>
				<Typography variant="h6">Select marker</Typography>
				<Stack
					direction="row"
					spacing={0.5}
					sx={{ alignItems: "center" }}>
					<IconButton onClick={onClose}>
						<XIcon />
					</IconButton>
				</Stack>
			</Stack>
			<Stack
				spacing={5}
				sx={{ overflowY: "auto", p: 3 }}>
				<Tabs
					onChange={(_, value: string) => {
						setTab(value);
					}}
					value={tab}>
					<Tab
						label="Marker"
						value="basic"
					/>
					<Tab
						label="Custom"
						value="custom"
					/>
				</Tabs>
				<Box
					sx={{
						display: "flex",
						flexWrap: "wrap",
						flexDirection: "column",
						height: "fit-content",
						maxWidth: "400px",
						width: "fit-content",
					}}>
					<ToggleButtonGroup
						orientation="vertical"
						color="primary"
						exclusive
						tabIndex={0}
						value={value}
						sx={{
							border: "none",
							boxShadow: "none",
							display: "flex",
							flexWrap: "wrap",
							maxHeight: "100px",
						}}
						onChange={(_, value: string) => {
							onChange(value);
						}}>
						{tab === "basic"
							? map(BASIC_MARKERS, ({ Icon, ...rest }, key) => (
									<ToggleButton
										key={key}
										value={key}>
										<Icon
											fontSize={24}
											{...rest}
										/>
									</ToggleButton>
								))
							: null}
					</ToggleButtonGroup>
					{tab === "custom" ? (
						<Stack spacing={5}>
							<FileDropzone
								caption={
									<Stack direction="row">
										<InfoIcon fontSize={24} />
										<Box sx={{ pl: "5px" }}>Max. 50x50px, .png format.</Box>
									</Stack>
								}
								maxFiles={1}
								onDrop={handleCoverDrop}
							/>
							<Card>
								<ToggleButtonGroup
									orientation="vertical"
									color="primary"
									exclusive
									value={value}
									sx={{
										border: "none",
										boxShadow: "none",
										display: "flex",
										flexWrap: "wrap",
										maxHeight: "100px",
									}}
									onChange={(_, value: string) => {
										onChange(value);
									}}>
									{(() => {
										return icons.map((icon, idx) => {
											const id = icon.id ?? `fallback-id-${idx}`;
											return (
												<ToggleButton
													key={id}
													value={id}
													style={{
														display: "flex",
														alignItems: "center",
														gap: "8px",
													}}>
													<img
														src={icon.iconUrl}
														width={32}
														height={32}
														alt={icon.name}
														style={{ display: "block" }}
													/>
													<span>{icon.size}</span>
												</ToggleButton>
											);
										});
									})()}
								</ToggleButtonGroup>
							</Card>
						</Stack>
					) : null}
				</Box>
			</Stack>
		</Drawer>
	);
}
